/**
 * Quick Fit Algorithm - Pure functions for aircraft-to-unit fit analysis
 *
 * Determines which units can physically accommodate a selected aircraft
 * based on door clearance, max dimension constraints, and available space.
 * Works with any source of dimensions (aircraft type catalog or individual aircraft).
 */

import { isOutdoorAreaType, isNonAircraftType } from '@/types/areas'
import type { AreaType } from '@/types/hangars'
import type { SpaceManagementArea, SpaceManagementUnit } from '@/types/space-management'
import type {
  FitAircraftDims,
  FitStatus,
  FailReason,
  QuickFitCandidate,
  QuickFitResult,
} from '@/types/quick-fit'

/** Margin threshold (feet) below which a fit is classified as "tight" */
const TIGHT_FIT_THRESHOLD = 3

/** Safety buffer applied to aircraft footprint (5%) */
const FOOTPRINT_SAFETY_BUFFER = 1.05

/** Capacity utilization percentage at which fit is classified as "tight" */
const CAPACITY_TIGHT_THRESHOLD = 70

// ============================================
// Core Fit Check
// ============================================

interface FitCheckResult {
  fitStatus: FitStatus
  failReasons: FailReason[]
  wingspanDoorMargin: number | null
  heightDoorMargin: number | null
  wingspanMaxMargin: number | null
  lengthMaxMargin: number | null
  tailHeightMaxMargin: number | null
  bufferedFootprint: number | null
  capacityPctAfterFit: number | null
}

/**
 * Check whether aircraft dimensions can fit in a unit.
 * Returns pass/fail with specific margins and failure reasons.
 */
export function checkUnitFit(
  unit: SpaceManagementUnit,
  dims: FitAircraftDims
): FitCheckResult {
  const failReasons: FailReason[] = []
  const isOutdoor = isOutdoorAreaType(unit.area_type as AreaType)

  // --- Door clearance (skip for outdoor areas) ---
  let wingspanDoorMargin: number | null = null
  let heightDoorMargin: number | null = null

  if (!isOutdoor) {
    if (unit.door_width != null) {
      wingspanDoorMargin = unit.door_width - dims.wingspan
      if (wingspanDoorMargin < 0) {
        failReasons.push('wingspan_exceeds_door')
      }
    }
    if (unit.door_height != null) {
      heightDoorMargin = unit.door_height - dims.height
      if (heightDoorMargin < 0) {
        failReasons.push('height_exceeds_door')
      }
    }
  }

  // --- Max dimension constraints (apply to all unit types) ---
  let wingspanMaxMargin: number | null = null
  let lengthMaxMargin: number | null = null
  let tailHeightMaxMargin: number | null = null

  if (unit.max_wingspan != null) {
    wingspanMaxMargin = unit.max_wingspan - dims.wingspan
    if (wingspanMaxMargin < 0) {
      failReasons.push('wingspan_exceeds_max')
    }
  }

  if (unit.max_length != null) {
    lengthMaxMargin = unit.max_length - dims.length
    if (lengthMaxMargin < 0) {
      failReasons.push('length_exceeds_max')
    }
  }

  if (unit.max_tail_height != null) {
    const effectiveTailHeight = dims.tailHeight ?? dims.height
    tailHeightMaxMargin = unit.max_tail_height - effectiveTailHeight
    if (tailHeightMaxMargin < 0) {
      failReasons.push('tail_height_exceeds_max')
    }
  }

  // --- Compute buffered footprint ---
  const rawFootprint = dims.footprintSqft ?? (dims.wingspan * dims.length)
  const bufferedFootprint = rawFootprint * FOOTPRINT_SAFETY_BUFFER
  let capacityPctAfterFit: number | null = null

  // --- Space availability ---
  if (unit.capacity_strategy === 'single') {
    if (unit.placed_aircraft_count > 0) {
      failReasons.push('unit_full')
    }
  } else if (unit.capacity_strategy === 'stackable' || unit.capacity_strategy === 'area_based') {
    if (unit.usable_sqft != null && unit.usable_sqft > 0) {
      const remaining = unit.usable_sqft - unit.occupied_sqft
      capacityPctAfterFit = ((unit.occupied_sqft + bufferedFootprint) / unit.usable_sqft) * 100
      if (remaining < bufferedFootprint) {
        failReasons.push('no_space')
      }
    }
  }

  // --- Determine fit status ---
  let fitStatus: FitStatus
  if (failReasons.length > 0) {
    fitStatus = 'no-fit'
  } else {
    const margins = [
      wingspanDoorMargin,
      heightDoorMargin,
      wingspanMaxMargin,
      lengthMaxMargin,
      tailHeightMaxMargin,
    ].filter((m): m is number => m != null)

    const minMargin = margins.length > 0 ? Math.min(...margins) : Infinity

    if (minMargin < TIGHT_FIT_THRESHOLD) {
      fitStatus = 'tight'
    } else if (capacityPctAfterFit != null && capacityPctAfterFit >= CAPACITY_TIGHT_THRESHOLD) {
      fitStatus = 'tight'
    } else {
      fitStatus = 'fits'
    }
  }

  return {
    fitStatus,
    failReasons,
    wingspanDoorMargin,
    heightDoorMargin,
    wingspanMaxMargin,
    lengthMaxMargin,
    tailHeightMaxMargin,
    bufferedFootprint,
    capacityPctAfterFit,
  }
}

// ============================================
// Fit Scoring (lower = better)
// ============================================

export function computeFitScore(
  unit: SpaceManagementUnit,
  dims: FitAircraftDims
): number {
  let score = 0

  if (unit.door_width != null && dims.wingspan > 0) {
    const doorMarginPct = (unit.door_width - dims.wingspan) / unit.door_width
    score += doorMarginPct * 30
  }

  if (unit.usable_sqft != null && unit.usable_sqft > 0) {
    const footprint = (dims.footprintSqft ?? (dims.wingspan * dims.length)) * FOOTPRINT_SAFETY_BUFFER
    const spaceRatio = footprint / unit.usable_sqft
    score += (1 - spaceRatio) * 40
  }

  score += unit.placed_aircraft_count * 20

  if (unit.status === 'maintenance') score += 100
  if (unit.status === 'leased') score += 50

  return score
}

// ============================================
// Main Evaluator
// ============================================

/**
 * Evaluate all units across all areas for given aircraft dimensions.
 */
export function evaluateQuickFit(
  areas: SpaceManagementArea[],
  dims: FitAircraftDims,
  displayName: string
): QuickFitResult {
  const candidates: QuickFitCandidate[] = []

  for (const area of areas) {
    if (isNonAircraftType(area.type as AreaType)) continue

    for (const unit of area.units) {
      const fitCheck = checkUnitFit(unit, dims)
      const fitScore = computeFitScore(unit, dims)

      candidates.push({
        unitId: unit.id,
        unitCode: unit.unit_code,
        unitName: unit.name,
        unitType: unit.type,
        capacityStrategy: unit.capacity_strategy,
        status: unit.status,

        areaId: area.id,
        areaName: area.name,
        areaCode: area.area_code,
        areaType: area.type,

        doorWidth: unit.door_width,
        doorHeight: unit.door_height,
        maxWingspan: unit.max_wingspan,
        maxLength: unit.max_length,
        maxTailHeight: unit.max_tail_height,
        usableSqft: unit.usable_sqft,

        wingspanDoorMargin: fitCheck.wingspanDoorMargin,
        heightDoorMargin: fitCheck.heightDoorMargin,
        wingspanMaxMargin: fitCheck.wingspanMaxMargin,
        lengthMaxMargin: fitCheck.lengthMaxMargin,
        tailHeightMaxMargin: fitCheck.tailHeightMaxMargin,

        placedAircraftCount: unit.placed_aircraft_count,
        assignedAircraftCount: unit.assigned_aircraft_count,
        occupiedSqft: unit.occupied_sqft,

        leaseClientName: unit.lease_client_name,

        bufferedFootprint: fitCheck.bufferedFootprint,
        capacityPctAfterFit: fitCheck.capacityPctAfterFit,

        fitStatus: fitCheck.fitStatus,
        fitScore,
        failReasons: fitCheck.failReasons,
      })
    }
  }

  const tierOrder: Record<FitStatus, number> = { fits: 0, tight: 1, 'no-fit': 2 }
  candidates.sort((a, b) => {
    const tierDiff = tierOrder[a.fitStatus] - tierOrder[b.fitStatus]
    if (tierDiff !== 0) return tierDiff
    return a.fitScore - b.fitScore
  })

  return {
    displayName,
    dims,
    candidates,
    summary: {
      totalUnits: candidates.length,
      fits: candidates.filter((c) => c.fitStatus === 'fits').length,
      tight: candidates.filter((c) => c.fitStatus === 'tight').length,
      noFit: candidates.filter((c) => c.fitStatus === 'no-fit').length,
    },
  }
}
